import { API_BASE_URL } from '../config';
const baseUrl= API_BASE_URL;
const createPrivateBookingService = (token) => {

  const makeRequest = async (url, method, body = null) => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "generic-error-handler": "enabled", // Based on your API headers
      
    };

    const config = {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    };

    try {
      const response = await fetch(`${baseUrl}${url}`, config);

      // Handle 204 No Content responses (common for DELETE)
      if (response.status === 204) {
        return { success: true, message: "Booking deleted successfully" };
      }

      const data = await response.json();

      if (!response.ok) {
        let errorMessages = [];
        if (data.errors && Array.isArray(data.errors)) {
          errorMessages = data.errors.map(
            (err) => err.message || JSON.stringify(err)
          );
        } else if (data.message) {
          errorMessages = [data.message];
        } else {
          errorMessages = ["Request failed"];
        }

        return {
          success: false,
          error: {
            message: errorMessages.join("\n"),
            status: response.status,
          },
        };
      }

      return { success: true, message: data.message, data: data.data };
    } catch (error) {
      return {
        success: false,
        error: {
          message: error.message || "Network error occurred",
          status: 0,
        },
      };
    }
  };

  return {
    /**
     * Create a new private booking
     * @param {Object} bookingData - The booking data to submit
     * @param {boolean} [sendMail=false] - Whether to send confirmation email
     * @returns {Promise<Object>} The API response
     */
    createPrivateBooking: async (bookingData, sendMail = false) => {
      const payload = {
        title: bookingData.title,
        customer_social_reason: bookingData.customer_social_reason,
        customer_first_name: bookingData.customer_first_name,
        customer_last_name: bookingData.customer_last_name,
        customer_email: bookingData.customer_email,
        customer_phone: bookingData.customer_phone,
        start_date: bookingData.start_date, // Format: "YYYY/MM/DD"
        start_hour: bookingData.start_hour, // Format: "HH:mm"
        end_date: bookingData.end_date, // Format: "YYYY/MM/DD"
        end_hour: bookingData.end_hour, // Format: "HH:mm"
        capacity: bookingData.capacity,
        services_equipments: bookingData.services_equipments,
        disposition_type: bookingData.disposition_type,
        place_id: bookingData.place_id,
        staff_id: bookingData.staff_id,
        send_mail: sendMail,
        // Optional fields
        start_date_dt: bookingData.start_date_dt,
        end_date_dt: bookingData.end_date_dt,
        start_hour_dt: bookingData.start_hour_dt,
        end_hour_dt: bookingData.end_hour_dt,
        place: bookingData.place,
        staff: bookingData.staff,
      };

      return await makeRequest("calendar/privatebooking", "POST", payload);
    },

    /**
     * Update an existing private booking
     * @param {string} bookingId - The ID of the booking to update
     * @param {Object} bookingData - The updated booking data
     * @param {boolean} [sendMail=false] - Whether to send confirmation email
     * @returns {Promise<Object>} The API response
     */
    updatePrivateBooking: async (bookingId, bookingData, sendMail = false) => {
      const payload = {
        ...bookingData,
        send_mail: sendMail,
      };

      return await makeRequest(
        `calendar/privatebooking/${bookingId}`,
        "PUT",
        payload
      );
    },

    /**
     * Delete a private booking
     * @param {string} bookingId - The ID of the booking to delete
     * @returns {Promise<Object>} The API response
     */
    deletePrivateBooking: async (bookingId) => {
      if (!bookingId.startsWith("CZW-PB")) {
        return {
          success: false,
          error: { message: "Invalid booking ID format" },
        };
      }

      return await makeRequest(
        `calendar/privatebooking/${bookingId}`,
        "DELETE"
      );
    },

    /**
     * Get booking details
     * @param {string} bookingId - The ID of the booking to fetch
     * @returns {Promise<Object>} The booking data
     */
    getPrivateBooking: async (bookingId) => {
      return await makeRequest(`calendar/privatebooking/${bookingId}`, "GET");
    },
  };
};

export default createPrivateBookingService;
